import React from 'react';
import {Formik, Form, Field} from 'formik';
import * as Yup from 'yup';
import {FormControl} from '../../components/form-control';
import {Input} from '../../components/input';
import {Button, ButtonVariant, ButtonSize, ButtonType} from '../../components/button';
import {ButtonLoader} from '../../components/button-loader';
import {FileUpload} from '../../components/file-upload';
import {CompanySize} from '../../models';

export interface CompanyDetailsFormData {
  companyName: string;
  companySize: string;
  industry: string;
  website: string;
  description: string;
  logoImg?: string;
  coverImage?: string;
  logoFile?: File;
  coverImageFile?: File;
}

interface CompanyDetailsFormProps {
  initialValues?: CompanyDetailsFormData;
  onSubmit: (values: CompanyDetailsFormData) => Promise<void>;
  onCancel: () => void;
}

const companyDetailsValidationSchema = Yup.object().shape({
  companyName: Yup.string().min(2, 'Company name must be at least 2 characters').required('Company name is required'),
  companySize: Yup.string(),
  industry: Yup.string(),
  website: Yup.string().url('Please enter a valid URL'),
  description: Yup.string().max(500, 'Description must be less than 500 characters'),
});

const defaultInitialValues: CompanyDetailsFormData = {
  companyName: '',
  companySize: '',
  industry: '',
  website: '',
  description: '',
  logoImg: '',
  coverImage: '',
  logoFile: undefined,
  coverImageFile: undefined,
};

const getSelectClassName = (hasError: boolean) => {
  const baseClasses = 'w-full px-4 py-3 border rounded-lg font-inherit bg-white transition-all duration-150 ease-in-out outline-none';
  if (hasError) {
    return `${baseClasses} border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200`;
  }
  return `${baseClasses} border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200`;
};

const getTextareaClassName = (hasError: boolean) => {
  const baseClasses =
    'w-full px-4 py-3 border rounded-lg font-inherit bg-white transition-all duration-150 ease-in-out outline-none placeholder:text-gray-500 placeholder:opacity-100 resize-none';
  if (hasError) {
    return `${baseClasses} border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-200`;
  }
  return `${baseClasses} border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-200`;
};

const handleLogoUpload = (file: File, setFieldValue: any) => {
  const previewUrl = URL.createObjectURL(file);
  setFieldValue('logoImg', previewUrl);
  setFieldValue('logoFile', file);
};

const handleCoverImageUpload = (file: File, setFieldValue: any) => {
  const previewUrl = URL.createObjectURL(file);
  setFieldValue('coverImage', previewUrl);
  setFieldValue('coverImageFile', file);
};

export const CompanyDetailsForm: React.FC<CompanyDetailsFormProps> = ({initialValues = defaultInitialValues, onSubmit, onCancel}) => {
  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-medium text-gray-900 mb-2">Company Details</h2>
        <p className="text-gray-600">Manage your company information and settings.</p>
      </div>

      <Formik
        initialValues={initialValues}
        enableReinitialize={true}
        validationSchema={companyDetailsValidationSchema}
        onSubmit={async (values, {setSubmitting}) => {
          try {
            await onSubmit(values);
          } finally {
            setSubmitting(false);
          }
        }}>
        {({values, errors, touched, isSubmitting, setFieldValue}) => (
          <Form className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <FormControl label="Company name" error={touched.companyName ? errors.companyName : undefined} required>
                <Field name="companyName">
                  {({field}: any) => (
                    <Input {...field} type="text" placeholder="Enter company name" error={!!(touched.companyName && errors.companyName)} />
                  )}
                </Field>
              </FormControl>

              <FormControl label="Industry" error={touched.industry ? errors.industry : undefined}>
                <Field name="industry">
                  {({field}: any) => (
                    <Input {...field} type="text" placeholder="Enter industry" error={!!(touched.industry && errors.industry)} />
                  )}
                </Field>
              </FormControl>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <FormControl label="Company URL" error={touched.website ? errors.website : undefined}>
                <Field name="website">
                  {({field}: any) => (
                    <Input {...field} type="url" placeholder="Enter your company url" error={!!(touched.website && errors.website)} />
                  )}
                </Field>
              </FormControl>

              <FormControl label="Company Size" error={touched.companySize ? errors.companySize : undefined}>
                <Field name="companySize">
                  {({field}: any) => (
                    <select {...field} className={getSelectClassName(!!(touched.companySize && errors.companySize))}>
                      <option value="" disabled>
                        Select company size
                      </option>
                      <option value={CompanySize.SMALL}>{CompanySize.SMALL}</option>
                      <option value={CompanySize.MEDIUM_SMALL}>{CompanySize.MEDIUM_SMALL}</option>
                      <option value={CompanySize.MEDIUM}>{CompanySize.MEDIUM}</option>
                      <option value={CompanySize.MEDIUM_LARGE}>{CompanySize.MEDIUM_LARGE}</option>
                      <option value={CompanySize.LARGE}>{CompanySize.LARGE}</option>
                      <option value={CompanySize.ENTERPRISE}>{CompanySize.ENTERPRISE}</option>
                    </select>
                  )}
                </Field>
              </FormControl>
            </div>

            <FormControl label="Company Description" error={touched.description ? errors.description : undefined}>
              <Field name="description">
                {({field}: any) => (
                  <textarea
                    {...field}
                    rows={4}
                    placeholder="Share your recruiting experience, specializations, and what motivates you to connect great talent with innovative companies..."
                    className={getTextareaClassName(!!(touched.description && errors.description))}
                  />
                )}
              </Field>
            </FormControl>

            <div className="grid grid-cols-2 gap-6">
              <FormControl label="Company Logo" error={touched.logoImg ? errors.logoImg : undefined}>
                <FileUpload
                  id="logoImg"
                  value={values.logoImg}
                  previewUrl={values.logoImg}
                  placeholder="Click here to upload"
                  accept="image/*"
                  onChange={(_, file) => handleLogoUpload(file, setFieldValue)}
                  error={!!(touched.logoImg && errors.logoImg)}
                />
              </FormControl>

              <FormControl label="Cover Image" error={touched.coverImage ? errors.coverImage : undefined}>
                <FileUpload
                  id="coverImage"
                  value={values.coverImage}
                  previewUrl={values.coverImage}
                  placeholder="Click here to upload"
                  accept="image/*"
                  onChange={(_, file) => handleCoverImageUpload(file, setFieldValue)}
                  error={!!(touched.coverImage && errors.coverImage)}
                />
              </FormControl>
            </div>

            <div className="flex justify-end space-x-4 pt-6">
              <Button
                type={ButtonType.BUTTON}
                variant={ButtonVariant.Secondary}
                size={ButtonSize.MD}
                onClick={onCancel}
                disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type={ButtonType.SUBMIT} variant={ButtonVariant.Primary} size={ButtonSize.MD} disabled={isSubmitting}>
                {isSubmitting ? <ButtonLoader /> : 'Save'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};
